import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { publicAPI } from '../lib/api';
import { 
  Search, 
  Tent, 
  Users, 
  ShieldCheck, 
  ArrowRight,
  MapPin,
  Star,
  CheckCircle2
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await publicAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative hero-gradient overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Content */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full text-sm text-primary font-medium">
                <ShieldCheck className="w-4 h-4" />
                South Africa's Trusted Camp Directory
              </div>
              
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-foreground">
                Find Verified{' '}
                <span className="text-primary">Camps</span> &{' '}
                <span className="text-campfire">Facilitators</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl">
                The trusted public directory connecting parents, schools, and organisations 
                with verified camping venues and professional facilitators across South Africa.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="rounded-full px-8"
                  onClick={() => navigate('/directory/camps')}
                  data-testid="hero-find-camps-btn"
                >
                  Find Camps
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8"
                  onClick={() => navigate('/directory/facilitators')}
                  data-testid="hero-find-facilitators-btn"
                >
                  Find Facilitators
                </Button>
              </div>

              {/* Stats */}
              {stats && (
                <div className="flex flex-wrap gap-8 pt-6">
                  <div>
                    <div className="text-3xl font-heading font-bold text-foreground">
                      {stats.total_camps}
                    </div>
                    <div className="text-sm text-muted-foreground">Listed Camps</div>
                  </div>
                  <div>
                    <div className="text-3xl font-heading font-bold text-foreground">
                      {stats.total_facilitators}
                    </div>
                    <div className="text-sm text-muted-foreground">Facilitators</div>
                  </div>
                  <div>
                    <div className="text-3xl font-heading font-bold text-campfire">
                      {stats.verified_facilitators + stats.verified_camps}
                    </div>
                    <div className="text-sm text-muted-foreground">Verified</div>
                  </div>
                </div>
              )}
            </div>

            {/* Hero Image */}
            <div className="lg:col-span-6 relative">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800"
                  alt="Camp site"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-card rounded-2xl p-4 shadow-xl border border-border hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Verified Profiles</div>
                    <div className="text-sm text-muted-foreground">Trust & safety first</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="font-heading font-semibold text-3xl md:text-4xl text-foreground mb-4">
              Why Use Campia?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We prioritise trust, verification, and safety for all stakeholders in the camping ecosystem.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={ShieldCheck}
              title="Verified Profiles"
              description="All profiles undergo verification. Look for the verified badge to identify trusted facilitators and camps."
            />
            <FeatureCard
              icon={Search}
              title="Easy Discovery"
              description="Search and filter by province, camp type, activities, and more. Find exactly what you're looking for."
            />
            <FeatureCard
              icon={Star}
              title="Moderated Reviews"
              description="Read genuine reviews from parents, schools, and other facilitators. All reviews are moderated for authenticity."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-primary rounded-3xl p-8 md:p-12 text-center">
            <h2 className="font-heading font-semibold text-3xl md:text-4xl text-primary-foreground mb-4">
              Are You a Camp or Facilitator?
            </h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              Get listed in South Africa's trusted camp directory. Increase your visibility 
              and connect with parents and schools looking for quality camping experiences.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                variant="secondary"
                className="rounded-full px-8"
                onClick={() => navigate('/register')}
                data-testid="cta-get-listed-btn"
              >
                Get Listed Today
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="rounded-full px-8 bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10"
                onClick={() => navigate('/about')}
              >
                Learn About Verification
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-card rounded-2xl p-6 border border-border/50 hover:shadow-lg transition-all duration-300">
    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-primary" />
    </div>
    <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{title}</h3>
    <p className="text-muted-foreground text-sm">{description}</p>
  </div>
);

export default HomePage;
